/**
 * Per-method OCS request budgets — the single source of truth for every OCS
 * client copy in this repo (#663 follow-up).
 *
 * #663 gave every OCS call one flat 20 s deadline and made a breach
 * non-retriable. Both halves of that are wrong against the live upstream, and
 * the numbers say so. Measured against reseller 1170 (parent Bridge4IP 246) on
 * 2026-08-25, three consecutive byte-identical requests each:
 *
 *   listSubscriber {accountId:5259}     12.6 s |  0.7 s | 13.6 s   (202 KB)
 *   listPrepaidPackageTemplate {}       17.1 s |  9.0 s |  0.7 s   ( 64 KB)
 *   getCustomerTariff 1170              88 s – 128 s               ( 3.4 MB)
 *
 * Re-measured the same day at 20:43Z, 25 runs each, and the picture changed in
 * a way that matters:
 *
 *   listSubscriber              p50 0.5 s   p95 0.7 s   max 0.7 s   (202 KB)
 *   listPrepaidPackageTemplate  p50 0.3 s   p95 2.5 s   max 2.8 s   ( 64 KB)
 *   getCustomerTariff           p50 2.8 s   p95 3.3 s   max 3.3 s   (3,439,729 B)
 *
 * Byte-identical response, 3,439,729 B both times, delivered in 2.8 s here and
 * in 88-128 s earlier. So the slowness is a property of the upstream's *state*,
 * not of the payload: OCS has fast windows and slow windows, and everything
 * inside a slow window is slow together. The size only sets how far a slow
 * window can stretch a given call, which is why the 3.4 MB method degrades to
 * two minutes while a 64 KB one degrades to seventeen seconds.
 *
 * That is a resilience problem end to end, so both fixes are budgets, not repairs.
 *
 * 1. `getCustomerTariff` needs a deadline above its worst window, and there is
 *    no smaller request to ask for instead. OCS takes the method as a bare
 *    integer (`assertBareIntParam`) and the REST route demonstrably ignores
 *    `?country=` and `?trafficType=` — both return byte-identical bodies, which
 *    is why `bounded-results.ts` filters in-process rather than pushing the
 *    filter down. That projection (3.4 MB -> ~232 KB) already bounds what a
 *    model is handed, and matters twice over given #810/#811 (Haiku miscounted
 *    a 55-object dump) — but it runs *after* the body arrives, so it cannot
 *    shorten the transfer by a millisecond. Hence 180 s, 1.4x the worst
 *    observation. One attempt, not three: a slow window lasts far longer than a
 *    backoff, so a retry lands in the same window and turns a three-minute
 *    failure into a nine-minute one. In a fast window the budget never fires.
 *
 * 2. Everything else gets a per-attempt deadline above every observation (30 s,
 *    1.75x the slowest) and a retry when it is breached anyway. Here a retry is
 *    worth it: these calls are short enough that a second attempt costs seconds
 *    rather than minutes, and the spread within a window is wide enough (0.7 s
 *    vs 13.6 s, byte-identical) that the next attempt often lands on the fast
 *    side. That is what turns a 20-fold spread into a stable p95.
 *
 * `totalMs` is the contract the caller can rely on: whatever the retries do,
 * the call either answers or throws inside that budget. It is checked before
 * every attempt and before every backoff sleep, so the budget is a ceiling
 * rather than an aspiration.
 *
 * Env overrides (unchanged semantics for `OCS_TIMEOUT_MS`, which raises the
 * per-attempt deadline for every method):
 *   OCS_TIMEOUT_MS        per-attempt deadline, all methods
 *   OCS_TOTAL_TIMEOUT_MS  total budget, all methods
 */

export interface OcsBudget {
  /** Deadline for a single HTTP attempt, including the body read. */
  attemptMs: number;
  /** Hard cap on attempts, backoff sleeps included in the total budget. */
  maxAttempts: number;
  /** Wall-clock ceiling for the whole call. The caller-facing guarantee. */
  totalMs: number;
}

/**
 * 30 s per attempt is 1.75x the slowest healthy observation (17.1 s), so the
 * jittery tail lands inside the first attempt. 75 s total leaves room for two
 * full attempts plus backoff, which is where the resilience actually comes
 * from.
 */
export const DEFAULT_OCS_BUDGET: OcsBudget = {
  attemptMs: 30_000,
  maxAttempts: 3,
  totalMs: 75_000,
};

/**
 * Methods whose measured behaviour does not fit the default. Keyed by the OCS
 * method name, not the MCP tool name, because the budget is a property of the
 * upstream call.
 */
export const OCS_METHOD_BUDGETS: Readonly<Record<string, OcsBudget>> = {
  // 3.4 MB, 88-128 s measured. 180 s is 1.4x the slowest observation. One
  // attempt: payload size does not vary, so a retry only doubles the wait.
  getCustomerTariff: { attemptMs: 180_000, maxAttempts: 1, totalMs: 180_000 },
};

/** Below this there is not enough budget left for an attempt to be worth starting. */
export const MIN_ATTEMPT_BUDGET_MS = 1_000;

const BACKOFF_BASE_MS = 500;

/**
 * Raised when a call exhausts its budget.
 *
 * Deliberately NOT an OcsApiError: it carries no OCS status code, and the
 * OCS-error branch of a retry loop must never swallow it.
 */
export class OcsTimeoutError extends Error {
  constructor(
    public readonly method: string,
    /** The per-attempt deadline that was breached. */
    public readonly timeoutMs: number,
    /** The total budget for the call, when one applied. */
    public readonly budgetMs: number = timeoutMs,
    /** How many HTTP attempts were made before giving up. */
    public readonly attempts: number = 1,
  ) {
    super(
      `[${method}] OCS request timed out after ${timeoutMs}ms` +
        (attempts > 1 ? ` on each of ${attempts} attempts` : "") +
        (budgetMs > timeoutMs ? ` (budget ${budgetMs}ms)` : "") +
        `. The upstream did not deliver a complete response in time — usually an ` +
        `oversized payload or a wedged upstream. Narrow the request, or raise ` +
        `OCS_TIMEOUT_MS / OCS_TOTAL_TIMEOUT_MS if this method is legitimately slower here.`,
    );
    this.name = "OcsTimeoutError";
  }
}

function envInt(name: string): number | undefined {
  const proc = (globalThis as Record<string, unknown>)["process"] as
    | { env?: Record<string, string | undefined> }
    | undefined;
  const parsed = Number(proc?.env?.[name]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Resolve the budget for a method: table entry or default, then env overrides.
 *
 * Read on every call rather than cached at module load, because
 * `tests/integration/setup-live-ocs.ts` and individual tests set
 * `OCS_TIMEOUT_MS` at runtime and expect it to take effect.
 */
export function ocsBudgetFor(method: string): OcsBudget {
  const base = OCS_METHOD_BUDGETS[method] ?? DEFAULT_OCS_BUDGET;
  const attemptMs = envInt("OCS_TIMEOUT_MS") ?? base.attemptMs;
  // A total budget below the per-attempt deadline would cut the first attempt
  // short, which is never what an override meant.
  const totalMs = Math.max(envInt("OCS_TOTAL_TIMEOUT_MS") ?? base.totalMs, attemptMs);
  return { attemptMs, maxAttempts: base.maxAttempts, totalMs };
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(finish, ms);
    function finish(): void {
      clearTimeout(timer);
      signal.removeEventListener("abort", finish);
      resolve();
    }
    signal.addEventListener("abort", finish, { once: true });
  });
}

/**
 * Race `work` against a wall-clock deadline.
 *
 * A plain `setTimeout` on purpose: `AbortSignal.timeout()` is native and is not
 * patched by vitest's fake timers, so a deadline built on it cannot be tested
 * without burning real seconds. The signal is still handed to `fetch` so the
 * socket is torn down rather than leaked — but this race, not the signal, is
 * the guarantee, because a signal does not bound the body read at all.
 */
async function withDeadline<T>(work: Promise<T>, deadline: OcsTimeoutError, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(deadline), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Run one OCS call under its budget, retrying only on a deadline breach.
 *
 * `attempt` receives an `AbortSignal` that fires when its own per-attempt
 * deadline expires or the total budget runs out; pass it to `fetch`. Anything
 * `attempt` throws that is not an `OcsTimeoutError` propagates untouched, so
 * OCS status-code handling stays where it already lives.
 *
 * Modelled on `sendWithRetry` in packages/carrier-ai/src/bedrock.ts (#807):
 * full jitter, sleeps bound to the same signal as the work, and every attempt
 * and sleep checked against the deadline *before* it starts — so a retry can
 * never run past the budget the caller was promised.
 */
export async function runWithBudget<T>(
  method: string,
  attempt: (signal: AbortSignal, attemptMs: number) => Promise<T>,
  budget: OcsBudget = ocsBudgetFor(method),
): Promise<T> {
  const deadlineAt = Date.now() + budget.totalMs;
  const budgetController = new AbortController();
  const budgetTimer = setTimeout(() => budgetController.abort(), budget.totalMs);

  let attempts = 0;
  let lastTimeout: OcsTimeoutError | undefined;

  try {
    for (let i = 0; i < budget.maxAttempts; i++) {
      const remaining = deadlineAt - Date.now();
      if (remaining < MIN_ATTEMPT_BUDGET_MS) break;

      // Never let an attempt outlive the total budget.
      const attemptMs = Math.min(budget.attemptMs, remaining);
      attempts++;

      const attemptController = new AbortController();
      const onBudgetAbort = () => attemptController.abort();
      budgetController.signal.addEventListener("abort", onBudgetAbort, { once: true });
      const attemptTimer = setTimeout(() => attemptController.abort(), attemptMs);
      const breach = new OcsTimeoutError(method, attemptMs, budget.totalMs, attempts);

      try {
        return await withDeadline(
          attempt(attemptController.signal, attemptMs),
          breach,
          attemptMs,
        );
      } catch (err) {
        if (!(err instanceof OcsTimeoutError)) throw err;
        lastTimeout = new OcsTimeoutError(method, attemptMs, budget.totalMs, attempts);
      } finally {
        clearTimeout(attemptTimer);
        budgetController.signal.removeEventListener("abort", onBudgetAbort);
        // The response body is fully read by the time an attempt resolves, so
        // aborting here only tears down a socket nobody is still reading.
        attemptController.abort();
      }

      if (i === budget.maxAttempts - 1) break;
      const backoff = Math.random() * BACKOFF_BASE_MS * 2 ** i;
      if (Date.now() + backoff + MIN_ATTEMPT_BUDGET_MS > deadlineAt) break;
      await sleep(backoff, budgetController.signal);
    }
  } finally {
    clearTimeout(budgetTimer);
  }

  throw (
    lastTimeout ??
    new OcsTimeoutError(method, budget.attemptMs, budget.totalMs, Math.max(attempts, 1))
  );
}
